import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth.service';

/** API paths that work without a session; a 401 from them is not a sign-out. */
const PUBLIC_API_PATHS = ['/badge/', '/auth/login', '/auth/register', '/auth/password-reset'];

function isApiCall(url: string): boolean {
  return url === environment.apiUrl || url.startsWith(`${environment.apiUrl}/`);
}

function isPublicApiPath(url: string): boolean {
  const path = url.slice(environment.apiUrl.length);
  return PUBLIC_API_PATHS.some((p) => path.startsWith(p));
}

function withBearer<T>(req: HttpRequest<T>, token: string | null): HttpRequest<T> {
  return token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;
}

/**
 * Attaches the bearer token to calls to the Pass API only (never to identity-v2: fetching the token there would
 * recurse), and only once there is a session, so anonymous pages do not call identity on every request.
 * On a 401 with an identity session, re-exchanges the token once and retries; a second 401, or a 401 on a
 * legacy session, clears the session and returns to /login with the page to come back to.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiCall(req.url)) {
    return next(req);
  }
  const authService = inject(AuthService);
  const router = inject(Router);
  if (!authService.hasSession) {
    return next(req);
  }
  const publicPath = isPublicApiPath(req.url);

  const signOut = (err: HttpErrorResponse) => {
    authService.clearSession();
    void router.navigate(['/login'], router.url.startsWith('/login') ? undefined : { queryParams: { returnUrl: router.url } });
    return throwError(() => err);
  };

  return from(authService.getValidToken().catch(() => null)).pipe(
    switchMap((token) =>
      next(withBearer(req, token)).pipe(
        catchError((err: unknown) => {
          if (!(err instanceof HttpErrorResponse) || err.status !== 401 || publicPath) {
            return throwError(() => err);
          }
          if (!authService.hasIdentitySession) {
            return signOut(err);
          }
          return from(authService.getValidToken(true).catch(() => null)).pipe(
            switchMap((fresh) =>
              fresh
                ? next(withBearer(req, fresh)).pipe(
                    catchError((retryErr: unknown) =>
                      retryErr instanceof HttpErrorResponse && retryErr.status === 401 ? signOut(retryErr) : throwError(() => retryErr),
                    ),
                  )
                : signOut(err),
            ),
          );
        }),
      ),
    ),
  );
};
